import { Link } from "react-router-dom";
import { Star, GitCompareArrows, Check } from "lucide-react";
import { useCompare } from "@/context/CompareContext";
import { cardImage } from "@/lib/img";
import { isNew } from "@/lib/isNew";
import SmartImage from "@/components/SmartImage";
import ShareButton from "@/components/ShareButton";

export default function ReviewCard({ review, index = 0, enableCompare = false }) {
  const { has, toggle } = useCompare();
  const selected = has(review.id);
  const fresh = isNew(review.created_at);
  return (
    <div className="relative group fade-up" style={{ animationDelay: `${Math.min(index * 60, 400)}ms` }}>
      {enableCompare && (
        <button
          data-testid="review-compare-toggle"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggle(review); }}
          className={`absolute top-3 left-3 z-10 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-bold transition-colors backdrop-blur ${selected ? "bg-brand text-white" : "bg-white/95 text-ink-800 hover:bg-white"}`}
        >
          {selected ? <Check className="w-3.5 h-3.5" /> : <GitCompareArrows className="w-3.5 h-3.5" />}
          {selected ? "Added" : "Compare"}
        </button>
      )}

      <Link to={`/reviews/${review.slug}`} data-testid="review-card"
        className="block bg-white rounded-xl border border-neutral-200 overflow-hidden h-full flex flex-col hover:shadow-md hover:-translate-y-1 transition-[transform,box-shadow] duration-200">
        <div className="relative aspect-[16/10] overflow-hidden bg-neutral-100">
          {review.image_url ? (
            <SmartImage src={cardImage(review.image_url, 600)} alt={review.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-neutral-300 font-display text-3xl">Review</div>
          )}
          <div className="absolute top-3 left-3 flex items-center gap-2">
            {review.review_type === "roundup" && (
              <span className="bg-ink-950 text-white text-xs font-bold px-2.5 py-1 rounded-md shadow-sm" data-testid="roundup-badge">Buying Guide</span>
            )}
            {fresh && (
              <span className="bg-emerald-500 text-white text-xs font-bold px-2.5 py-1 rounded-md shadow-sm inline-flex items-center gap-1" data-testid="new-badge">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> New
              </span>
            )}
          </div>
          {review.rating != null && (
            <span className="absolute top-3 right-3 flex items-center gap-1 bg-white/95 backdrop-blur text-ink-950 text-xs font-bold px-2 py-1 rounded-md shadow-sm">
              <Star className="w-3 h-3 fill-brand text-brand" /> {Number(review.rating).toFixed(1)}
            </span>
          )}
        </div>
        <div className="p-5 flex flex-col flex-1">
          {review.category && <span className="text-xs tracking-[0.15em] uppercase font-bold text-brand mb-2">{review.category}</span>}
          <h3 className="font-display font-semibold text-lg text-ink-900 leading-snug line-clamp-2 mb-2 group-hover:text-brand transition-colors">{review.title}</h3>
          <p className="text-sm text-neutral-500 line-clamp-4">{review.summary}</p>
          <span className="mt-4 text-sm font-semibold text-ink-950 group-hover:text-brand transition-colors">Read review →</span>
        </div>
      </Link>
      <div className="absolute bottom-4 right-4">
        <ShareButton path={`/reviews/${review.slug}`} title={review.title} image={review.image_url} testid="review-card-share" />
      </div>
    </div>
  );
}
